// src/app/features/admin/useAdminTradeRequests.js
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  adminListTradeRequests,
  adminApproveTradeRequest,
  adminRejectTradeRequest,
} from "./adminApi.js";

const DEFAULT_LIMIT = 20;

function getErrMsg(err) {
  const data = err?.response?.data;
  if (!data) return err?.message || "Request failed.";
  if (typeof data.detail === "string") return data.detail;
  if (Array.isArray(data.detail)) return data.detail?.[0]?.msg || "Validation error.";
  return "Request failed.";
}

// Optional: normalize market inputs like "BTC/USD" -> "BTCUSD"
function normalizeMarket(market) {
  return String(market || "")
    .trim()
    .toUpperCase()
    .replace(/\s+/g, "")
    .replace(/[^\w]/g, ""); // strips "/" "-" etc.
}

function normalizeStatus(status) {
  const s = String(status || "").trim().toUpperCase();

  // ✅ Backward-compat with any old UI values
  if (s === "PENDING_MODERATION") return "PENDING";
  if (s === "PENDING_APPROVAL") return "PENDING";

  // ✅ Allow blank/"ALL" for server-side "no filter"
  if (!s || s === "ALL") return "";

  // ✅ Only server-supported statuses (based on your backend constants)
  if (s === "PENDING" || s === "APPROVED" || s === "REJECTED") return s;

  // Unknown => treat as no filter (prevents silently filtering everything out)
  return "";
}

function normalizeDirection(direction) {
  const d = String(direction || "").trim().toUpperCase();
  if (!d || d === "ALL") return "";
  if (d === "UP" || d === "DOWN") return d;
  return "";
}

export default function useAdminTradeRequests(initial = {}) {
  const [filters, setFilters] = useState({
    // ✅ IMPORTANT: default must match backend: PENDING | APPROVED | REJECTED
    status: initial.status || "PENDING",
    market: initial.market || "",
    direction: initial.direction || "",
  });

  const [limit, setLimit] = useState(initial.limit || DEFAULT_LIMIT);
  const [offset, setOffset] = useState(initial.offset || 0);

  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);

  const [loading, setLoading] = useState(false);
  const [actionBusyId, setActionBusyId] = useState(null);
  const [error, setError] = useState("");

  const page = useMemo(() => Math.floor(offset / limit) + 1, [offset, limit]);
  const pageCount = useMemo(() => Math.max(1, Math.ceil(total / limit)), [total, limit]);

  const params = useMemo(() => {
    const p = { limit, offset };

    const status = normalizeStatus(filters.status);
    const market = filters.market ? normalizeMarket(filters.market) : "";
    const direction = normalizeDirection(filters.direction);

    // Only include filters if they are meaningful; avoids server-side over-filtering
    if (status) p.status = status;
    if (market) p.market = market;
    if (direction) p.direction = direction;

    return p;
  }, [limit, offset, filters]);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const data = await adminListTradeRequests(params);

      const rawItems = Array.isArray(data?.items) ? data.items : [];

      // ✅ Normalize server statuses so the UI can reliably enable Approve/Reject
      const normItems = rawItems.map((r) => ({
        ...r,
        status: normalizeStatus(r?.status) || String(r?.status || "").toUpperCase(),
        market: String(r?.market || "").toUpperCase(),
        direction: normalizeDirection(r?.direction) || String(r?.direction || "").toUpperCase(),
      }));

      setItems(normItems);
      setTotal(typeof data?.total === "number" ? data.total : 0);

      // keep pagination stable
      if (typeof data?.limit === "number") setLimit(data.limit);
      if (typeof data?.offset === "number") setOffset(data.offset);
    } catch (err) {
      setItems([]);
      setTotal(0);
      setError(getErrMsg(err));
    } finally {
      setLoading(false);
    }
  }, [params]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const applyFilters = useCallback((patch) => {
    setOffset(0);
    setFilters((prev) => ({ ...prev, ...patch }));
  }, []);

  const resetFilters = useCallback(() => {
    setOffset(0);
    setFilters({ status: "PENDING", market: "", direction: "" });
  }, []);

  const setPerPage = useCallback((n) => {
    const newLimit = Math.min(100, Math.max(1, Number(n) || DEFAULT_LIMIT));
    setLimit(newLimit);
    setOffset(0);
  }, []);

  const nextPage = useCallback(() => {
    if (page < pageCount) setOffset((x) => x + limit);
  }, [page, pageCount, limit]);

  const prevPage = useCallback(() => {
    if (page > 1) setOffset((x) => Math.max(0, x - limit));
  }, [page, limit]);

  const approve = useCallback(
    async (id) => {
      if (!id) return;
      setActionBusyId(id);
      setError("");
      try {
        await adminApproveTradeRequest(id);
        await refresh();
      } catch (err) {
        setError(getErrMsg(err));
      } finally {
        setActionBusyId(null);
      }
    },
    [refresh]
  );

  const reject = useCallback(
    async (id) => {
      if (!id) return;
      setActionBusyId(id);
      setError("");
      try {
        await adminRejectTradeRequest(id);
        await refresh();
      } catch (err) {
        setError(getErrMsg(err));
      } finally {
        setActionBusyId(null);
      }
    },
    [refresh]
  );

  return {
    items,
    total,
    loading,
    error,
    actionBusyId,
    page,
    pageCount,
    nextPage,
    prevPage,
    setPerPage,
    limit, // ✅ expose so per-page select can be controlled

    // ✅ expose normalized filters for consistent UI behavior
    filters: {
      ...filters,
      status: normalizeStatus(filters.status) || filters.status,
      direction: normalizeDirection(filters.direction) || filters.direction,
    },

    applyFilters,
    resetFilters,
    refresh,
    approve,
    reject,
  };
}
